import React, { useState, useCallback, useRef, useEffect } from "react";
import styles from "./loggedInNavItem.module.scss";
import { useAppStore } from "@/providers/app";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import Item from "./item";
import GetTurfTownModal from "../GetTurfTownModal";
import classNames from "classnames";
import { truncateBracketedText, truncateWords } from "@/utils";

interface LoggedInNavItemPropType {
  noDropdown?: boolean;
  onClick?: () => void;
  onClickLogout?: () => void;
}

const LoggedInNavItem = ({
  noDropdown = false,
  onClick,
  onClickLogout,
}: LoggedInNavItemPropType) => {
  const user = useAppStore((state) => state.user);
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setModal] = useState<string>("");

  const toggleDropdown = () => {
    if (!noDropdown) {
      setIsOpen(!isOpen);
    } else {
      onClick && onClick();
    }
  };

  const close = useCallback(() => {
    setModal("");
  }, []);

  const dropdownRef = useRef<HTMLDivElement>(null); // Ref to track the dropdown

  const handleOutsideClick = (event: MouseEvent) => {
    // Check if click is outside the dropdown
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false); // Close dropdown if click is outside
    }
  };

  useEffect(() => {
    // Add event listener to detect outside clicks
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      // Cleanup the event listener when the component unmounts
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <>
      <div
        ref={dropdownRef}
        onClick={toggleDropdown}
        className={styles["container"]}
      >
        <div
          className={classNames(
            styles["item"],
            styles["cursor-pointer"],
            styles["gap-12"]
          )}
        >
          <Image
            width={15}
            height={10}
            src={"/icons/menu_icon.png"}
            alt={"loggedin icon"}
          />
          {user && (
            <div
              className={classNames(
                styles["row"],
                styles["align-center"],
                styles["gap-10"],
                styles["relative"]
              )}
            >
              <p className={classNames(styles.username)}>
                {truncateWords(user?.name, 6)}
              </p>
              <div className={styles.round_image}>
                <Image
                  objectFit="cover"
                  layout="fill"
                  src={
                    user?.profile_picture
                      ? user?.profile_picture
                      : "/images/empty_user_profile.webp"
                  }
                  alt={"profile"}
                />
              </div>
            </div>
          )}
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: 1 }}
              exit={{ opacity: 0 }} // Only fades out
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={styles["drop-down"]}
            >
              <motion.div
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
                  },
                }}
              >
                <motion.div
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                >
                  <Item title="Your Games" url="/your-games" link />
                </motion.div>

                {/* <motion.div
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                >
                  <Item title="Turf ID" url="" user={user?.name} />
                </motion.div> */}

                <motion.div
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                >
                  <Item
                    title="Logout"
                    url=""
                    link={false}
                    onClick={() => {
                      setIsOpen(false);
                      onClickLogout && onClickLogout();
                    }}
                  />
                </motion.div>

                <motion.div
                  className={styles["line"]}
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                />

                <motion.div
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                >
                  <Item
                    title="Download Turf Town"
                    url=""
                    blueText
                    link={false}
                    lastItem
                    onClick={() => {
                      setIsOpen(false);
                      setModal("download");
                    }}
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <GetTurfTownModal
        show={showModal == "download"}
        onClose={() => {
          setModal("");
        }}
      />
    </>
  );
};

export default LoggedInNavItem;
